import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type Query } from '@tanstack/react-query';

import { apiClient } from '../../../core/api/client';
import {
    STARE_NEAPROBAT,
    protocolulSePoateCere,
    stareDinRaspuns,
    type StareProtocol
} from '../utils/protocol-descarcare';
import { useAuthProviderV2 } from '../../../providers/provider-hooks';
import { trackProtocolDownloaded } from '@/telemetry/posthog-events';

/**
 * Aduce protocolul de colaborare si il preda browserului (SP-03, pasul 4 din 4).
 *
 * 🔴 CEREREA O FACE APLICATIA, nu browserul prin `<a href download>`. Nu e un
 * moft de arhitectura: un link care esueaza **nu spune paginii nimic** —
 * browserul navigheaza, iar noi nu aflam nici codul de raspuns, nici ca n-a
 * venit nimic. Cu link simplu, „nu s-a descarcat" arata IDENTIC cu
 * „s-a descarcat", si starea de eroare din directie ar fi un desen care nu se
 * poate declansa. Constrangerea e a lui S1, si e conditia ca ecranul 4 sa existe.
 *
 * 🔴 DE CE `apiClient` (axios) SI NU `fetch` GOL. `fetch` ar fi trebuit sa-si
 * lipeasca singur `Authorization`, si ar fi ratat exact ce face clientul comun:
 * reimprospatarea proactiva a tokenului si reluarea cererii dupa un 401. Un
 * token expirat ar fi aterizat in starea „a esuat" — un ecran de eroare rosu
 * pentru ceva ce se repara singur. Verificarea statusului, adica partea care
 * conteaza in constrangerea de mai sus, e la fel de explicita aici.
 *
 * ⚠️ `validateStatus: () => true` e OBLIGATORIU. Fara el axios arunca la 404,
 * iar 404 nu e o eroare aici — e starea „nu e disponibil inca" din directie, si
 * are nevoie de corpul raspunsului ca sa se distinga de „ruta nu e pornita".
 *
 * ⚠️ Se cere la MONTARE, nu la clic, si asta e un compromis asumat: ca sa stim
 * daca butonul e activ (starea 1) sau dezactivat (starea 2) trebuie sa fi
 * intrebat serverul inainte ca omul sa apese. Costul e ca fisierul se aduce si
 * pentru cine nu apasa; castigul e ca toate cele patru stari sunt reale, iar
 * clicul nu mai are latenta. Daca protocolul real se dovedeste mare, asta se
 * remasoara — un HEAD ar fi ieftin, dar un HEAD n-are corp, deci ar pierde
 * distinctia dintre cele doua 404-uri.
 *
 * 🔴 DOUA LIMITE puse contractului de mai sus pe 2026-10-07, dupa masuratoare (14 zile: 540 de
 * 404 si 41 de 409 pe ruta asta, plus pasul 4 bifat fara ca omul sa fi atins ceva):
 *
 *   1. POARTA. Cererea pleaca doar cu `schoolApprovedForCurrentEdition === true` in token
 *      (`protocolulSePoateCere`). Backendul consemneaza protocolul ca DESCARCAT la fiecare 200,
 *      deci o cerere „de verificare" e de fapt o scriere; iar inainte de aprobare raspunsul se
 *      stie oricum din token. Cu poarta inchisa starea e „neaprobat", fara nicio cerere.
 *   2. `laCerere`. „La montare" e contractul ECRANELOR care predau protocolul (cardul de la pasul
 *      4, «Acasă»). Meniul de utilizator se monteaza pe fiecare pagina autentificata: acolo
 *      hook-ul NU cere nimic pana cand omul apasa itemul (`cereSiDescarca`).
 *
 * O SINGURA cerere pe sesiune de lucru, nu una per componenta: toti consumatorii citesc aceeasi
 * intrare din React Query (`cheieProtocol`), tinuta proaspata `PROASPAT_MS`.
 */

/** `null` = inca nu stim; ecranul arata starea de incarcare, nu una din cele patru. */
export type StareIncarcare = StareProtocol | null;

interface Optiuni {
    /**
     * `true` = nu cere nimic la montare; cererea pleaca abia din `cereSiDescarca`. Pentru locurile
     * care sunt mereu pe ecran (meniul de utilizator), unde „la montare" ar insemna „la fiecare pagina".
     */
    laCerere?: boolean;
}

interface Rezultat {
    stare: StareIncarcare;
    /** Preda octetii browserului. Fara efect cat timp starea nu e „gata". */
    descarca: () => void;
    /** Butonul «Încearcă din nou» din starea 4. */
    reincearca: () => void;
    /**
     * Cere protocolul (din cache daca e proaspat) si, daca e „gata", il preda browserului. Intoarce
     * starea, ca apelantul sa poata spune omului de ce n-a primit nimic. Nu respinge niciodata.
     */
    cereSiDescarca: () => Promise<StareProtocol>;
}

/** Ce tine cache-ul: starea ecranului + octetii, cand exista. */
interface DateProtocol {
    stare: StareProtocol;
    continut: Blob | null;
}

/**
 * Cat ramane „proaspat" un protocol adus cu succes: in intervalul asta, alt ecran care il cere il
 * ia din cache. PDF-ul se genereaza din datele scolii, deci nu se tine mai mult — o corectie facuta
 * in «Detalii școală» trebuie sa apara in fisier fara reincarcarea paginii.
 */
const PROASPAT_MS = 5 * 60 * 1000;

/** Pe utilizator: dupa logout + login cu alt cont, cache-ul nu serveste protocolul altei scoli. */
const cheieProtocol = (userId: string | undefined) =>
    ['schools', 'own', 'protocol-de-colaborare', userId ?? null] as const;

/**
 * Doar un „gata" e proaspat. Orice alta stare (404, 409, esec) se recere la urmatoarea montare sau
 * la urmatorul clic — altfel o eroare de retea ar ramane „raspunsul" timp de cinci minute.
 */
const catEProaspat = (query: Query<DateProtocol, Error, DateProtocol, ReturnType<typeof cheieProtocol>>) =>
    query.state.data?.stare.stare === 'gata' ? PROASPAT_MS : 0;

/** Cererea in sine. NU arunca: orice rezultat, inclusiv reteaua cazuta, e o stare a ecranului. */
async function cereProtocolul(): Promise<DateProtocol> {
    try {
        const raspuns = await apiClient.get('/api/schools/own/protocol-de-colaborare', {
            responseType: 'blob',
            // Vezi comentariul de sus: 404 e o STARE, nu o exceptie.
            validateStatus: () => true
        });

        const corp: Blob | undefined = raspuns.data;
        // Cu `responseType: 'blob'` si corpul unui 404, `size` e singura cale
        // sincrona de a sti daca serverul a spus ceva sau a tacut.
        const areCorp = (corp?.size ?? 0) > 0;

        // Antetele axios se traduc in `Headers` AICI, ca modulul pur sa ramana
        // fara dependinte si testabil fara retea.
        const antete = new Headers();
        Object.entries(raspuns.headers ?? {}).forEach(([cheie, valoare]) => {
            if (typeof valoare === 'string') antete.set(cheie, valoare);
        });

        const stare = stareDinRaspuns(raspuns.status, antete, areCorp);

        /* Diagnosticul care nu incape pe ecran. Cele doua 404-uri arata la fel
           pentru om — si asa trebuie — dar pentru cine masoara sunt
           lucruri opuse: „scoala n-are protocol" e un rezultat, „ruta nu e
           pornita" inseamna ca n-am masurat nimic. */
        if (stare.stare === 'indisponibil' && stare.motiv === 'ruta-inactiva') {
            console.warn(
                '[protocol] 404 CU corp (handlerul global): ruta nu exista pe mediul asta '
                + '(proprietatea de backend e oprita). Nu inseamna ca scoala n-are protocol.'
            );
        }

        return { stare, continut: stare.stare === 'gata' && corp ? corp : null };
    } catch (eroare) {
        // Ajunge aici doar ce nu e raspuns HTTP: retea cazuta, cerere anulata.
        console.error('[protocol] cererea n-a ajuns la server:', eroare);
        return { stare: { stare: 'esuat', status: null }, continut: null };
    }
}

function predaBrowserului(continut: Blob, nume: string): void {
    const url = URL.createObjectURL(continut);
    const legatura = document.createElement('a');
    legatura.href = url;
    legatura.download = nume;
    document.body.appendChild(legatura);
    legatura.click();
    document.body.removeChild(legatura);
    // Fara revocare, obiectul URL traieste cat pagina.
    URL.revokeObjectURL(url);
    trackProtocolDownloaded();

    /* 🔴 AICI NU SE MARCHEAZA NIMIC, deliberat. Am predat octetii browserului
       si nu vedem ce urmeaza: salvat, anulat din dialog, deschis intr-un
       vizualizator si uitat. O bifa ar insemna „ai apasat butonul", nu „ai
       fisierul" — iar omul care vede bifa si nu gaseste fisierul are un ecran
       care il contrazice. Decizia (2) a owner-ului; o urma reala ar putea veni
       doar din backend, la servire, si S0 a decis ca nu in SP-03. */
}

export function useProtocolDeColaborare({ laCerere = false }: Optiuni = {}): Rezultat {
    const { userInfo, handleTokenRefresh } = useAuthProviderV2();
    const queryClient = useQueryClient();
    const aprobat = protocolulSePoateCere(userInfo);
    const queryKey = cheieProtocol(userInfo?.userId);

    // p8: dupa 200, backendul a marcat DEJA `protocolDescarcatDt` la SERVIRE
    // (`ProtocolDeColaborareController`, `alScoliiMele`) -- refresh-ul doar aduce claim-ul PROASPAT
    // in token. Prin ref-uri, ca functia de cerere sa ramana STABILA; si o singura data per cerere
    // reala (nu per componenta care citeste cache-ul), doar cat claim-ul nu e deja `true`.
    const refreshRef = useRef(handleTokenRefresh);
    const descarcatInToken = useRef(userInfo?.protocolDescarcat === true);
    useEffect(() => {
        refreshRef.current = handleTokenRefresh;
        descarcatInToken.current = userInfo?.protocolDescarcat === true;
    }, [handleTokenRefresh, userInfo?.protocolDescarcat]);

    const queryFn = useCallback(async (): Promise<DateProtocol> => {
        const date = await cereProtocolul();
        // Fire-and-forget, deliberat: un refresh esuat nu transforma predarea
        // intr-o poarta -- tokenul ramane cu claim-ul vechi, ceea ce e onest
        // (protocolul chiar n-a fost confirmat inca la nivel de token).
        if (date.stare.stare === 'gata' && !descarcatInToken.current) {
            void refreshRef.current(true);
        }
        return date;
    }, []);

    const { data, isFetching, refetch } = useQuery({
        queryKey,
        queryFn,
        // Poarta + modul lenes: fara scoala aprobata sau in meniu, NU pleaca nimic la montare.
        enabled: aprobat && !laCerere,
        staleTime: catEProaspat,
        gcTime: PROASPAT_MS,
        // `cereProtocolul` nu arunca; `false` explicit, ca o schimbare de implicit sa nu dubleze cererea.
        retry: false,
        // Implicitele aplicatiei (`query-client.ts`) sunt „recere mereu la montare"; aici montarea
        // recere doar ce nu mai e proaspat. Nici revenirea in fila, nici reconectarea nu recer.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });

    // Cat timp o cerere e in zbor (prima sau o reincercare) nu afirmam nimic: `null`.
    const stare: StareIncarcare = !aprobat
        ? STARE_NEAPROBAT
        : isFetching
            ? null
            : data?.stare ?? null;

    const descarca = useCallback(() => {
        if (!aprobat || data?.stare.stare !== 'gata' || !data.continut) return;
        predaBrowserului(data.continut, data.stare.nume);
    }, [aprobat, data]);

    const reincearca = useCallback(() => {
        // `refetch` ocoleste `enabled`, deci poarta se verifica si aici.
        if (aprobat) void refetch();
    }, [aprobat, refetch]);

    const cereSiDescarca = useCallback(async (): Promise<StareProtocol> => {
        if (!aprobat) return STARE_NEAPROBAT;
        try {
            // `fetchQuery` intoarce din cache ce e proaspat si se lipeste de o cerere deja in zbor.
            const date = await queryClient.fetchQuery({
                queryKey,
                queryFn,
                staleTime: catEProaspat,
                gcTime: PROASPAT_MS,
                retry: false
            });
            if (date.stare.stare === 'gata' && date.continut) {
                predaBrowserului(date.continut, date.stare.nume);
            }
            return date.stare;
        } catch (eroare) {
            // `cereProtocolul` nu arunca; aici ajunge doar o cerere anulata de React Query (de
            // exemplu cache-ul golit la deconectare). Tot o stare, nu o promisiune respinsa.
            console.error('[protocol] cererea a fost intrerupta:', eroare);
            return { stare: 'esuat', status: null };
        }
        // `queryKey` e un tablou nou la fiecare randare; identitatea lui e `userInfo?.userId`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aprobat, queryClient, queryFn, userInfo?.userId]);

    return { stare, descarca, reincearca, cereSiDescarca };
}
